import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AskJDSNavbar } from './AskJDSNavbar';
import { Footer } from '@/components/shared/Footer';

interface PageLayoutProps {
  children: ReactNode;
  /**
   * The navbar to render. Defaults to `<AskJDSNavbar />`, which since the
   * 2026-09-11 cutover is the LAW-SCHOOL context navbar.
   *
   * AN OVERRIDE, NOT A FORK. Before this prop existed, every page reaching for a
   * different navbar had to abandon PageLayout entirely and re-declare the
   * flex/main/footer shell — which is how BarPracticePage came to hand-roll its
   * own navbar + `<main>` + `<Footer />` shell and then kept its own
   * scroll-to-top behaviour (it does not have any). Three pages were about to
   * do the same for the bar context.
   *
   * WHY THE DEFAULT IS A LAW-SCHOOL NAVBAR AND THAT IS NOT A BUG: PageLayout's
   * remaining callers are the law-school marketing pages plus a handful of
   * legal/utility pages (/terms, /privacy, /disclaimer, …) where "Features /
   * Outlines / Pricing" is still the right frame. A page in another product
   * context passes its own navbar rather than inheriting this one silently.
   */
  navbar?: ReactNode;
}

export default function PageLayout({ children, navbar }: PageLayoutProps) {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      {navbar ?? <AskJDSNavbar />}
      <main className="flex-grow pt-[calc(4rem+env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom)] md:pb-0">
        {children}
      </main>
      <Footer />
    </div>
  );
}
